import { useRef, useState } from "react";
import api from "@/lib/api";
import { pickAudioMime, extForMime } from "@/lib/audio";
import { Button } from "@/components/ui/button";
import { Mic, Square, Loader2, RotateCcw, Play } from "lucide-react";
import { toast } from "sonner";

// Records audio via MediaRecorder, uploads for transcription, returns { blob, transcript, file }
export default function AudioRecorder({ onComplete, testid = "audio-recorder", language = "pt" }) {
  const [state, setState] = useState("idle"); // idle | recording | processing | done
  const [seconds, setSeconds] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const mimeRef = useRef("");
  const urlRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickAudioMime();
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mimeRef.current = mr.mimeType || mime || "";
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data && e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => stream.getTracks().forEach((t) => t.stop());
      mediaRef.current = mr;
      mr.start();
      setState("recording");
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      console.error("[audio] getUserMedia failed", e);
      toast.error("Não foi possível acessar o microfone.");
    }
  };

  const stop = async () => {
    clearInterval(timerRef.current);
    const mr = mediaRef.current;
    if (!mr) return;
    setState("processing");
    mr.onstop = async () => {
      mr.stream.getTracks().forEach((t) => t.stop());
      const type = mr.mimeType || mimeRef.current || "audio/webm";
      const ext = extForMime(type);
      const blob = new Blob(chunksRef.current, { type });
      if (urlRef.current) { try { URL.revokeObjectURL(urlRef.current); } catch (e) {} }
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      setBlobUrl(url);
      const fname = `gravacao.${ext}`;
      console.info("[audio] recorded", { ua: navigator.userAgent, blobType: type, ext, size: blob.size });
      let up = null;
      try {
        const upFd = new FormData(); upFd.append("file", blob, fname);
        up = (await api.post("/files/upload", upFd, { headers: { "Content-Type": "multipart/form-data" } })).data;
      } catch (e) {
        console.error("[audio] upload failed", e);
        setState("done");
        toast.error("Falha ao enviar o áudio. Tente novamente.");
        return;
      }
      let transcript = "";
      try {
        const trFd = new FormData(); trFd.append("file", blob, fname);
        transcript = (await api.post("/ai/transcribe", trFd, { headers: { "Content-Type": "multipart/form-data" } })).data.text || "";
        toast.success("Áudio transcrito. Revise e complemente antes de enviar.");
      } catch (e) {
        console.error("[audio] transcription failed", e);
        toast.error("Não foi possível transcrever. Você pode digitar manualmente.");
      }
      setState("done");
      onComplete?.({ blob, transcript, file: up });
    };
    mr.stop();
  };

  const reset = () => {
    setState("idle");
    setSeconds(0);
    setBlobUrl(null);
  };

  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

  return (
    <div className="rounded-2xl border border-[#E0F2FE] bg-[#F8FAFC] p-4" data-testid={testid}>
      <div className="flex items-center gap-4">
        {state === "recording" ? (
          <div className="flex items-center gap-1.5 h-9">
            {[...Array(9)].map((_, i) => (
              <span key={i} className="w-1.5 rounded-full bg-[#0F766E] animate-soft-pulse"
                style={{ height: `${10 + ((i * 7 + seconds * 3) % 26)}px`, animationDelay: `${i * 90}ms` }} />
            ))}
          </div>
        ) : (
          <div className="w-11 h-11 rounded-full bg-[#0F766E]/10 grid place-items-center">
            <Mic size={20} className="text-[#0F766E]" />
          </div>
        )}
        <div className="flex-1">
          <div className="text-sm font-semibold text-[#1F3B4D]">
            {state === "idle" && "Toque para gravar"}
            {state === "recording" && "Gravando…"}
            {state === "processing" && "Transcrevendo…"}
            {state === "done" && "Gravação pronta"}
          </div>
          <div className="text-xs text-[#94A3B8]">{state === "recording" ? mmss : "Áudio simples e intuitivo"}</div>
        </div>

        {state === "idle" && (
          <Button onClick={start} data-testid={`${testid}-start`} className="rounded-full bg-[#0F766E] hover:bg-[#0d6259] gap-2">
            <Mic size={16} /> Gravar
          </Button>
        )}
        {state === "recording" && (
          <Button onClick={stop} data-testid={`${testid}-stop`} className="rounded-full bg-[#EF4444] hover:bg-[#dc2626] gap-2">
            <Square size={15} /> Parar
          </Button>
        )}
        {state === "processing" && <Loader2 className="animate-spin text-[#0F766E]" size={20} />}
        {state === "done" && (
          <div className="flex items-center gap-2">
            {blobUrl && (
              <Button variant="outline" size="icon" className="rounded-full" onClick={async () => { try { await audioRef.current?.play(); } catch (e) { console.error("[audio] play failed", e); toast.error("Não foi possível reproduzir o áudio."); } }} data-testid={`${testid}-play`}>
                <Play size={15} />
              </Button>
            )}
            <Button variant="outline" size="icon" className="rounded-full" onClick={reset} data-testid={`${testid}-reset`}>
              <RotateCcw size={15} />
            </Button>
          </div>
        )}
      </div>
      {blobUrl && <audio ref={audioRef} src={blobUrl} className="hidden" />}
    </div>
  );
}
